import { Component } from '@theme/component';
import { resolveLabelAnchor, resolveMappingLabelAnchor, calcLabelFontSize, loadBadgeImage } from '@theme/badge-canvas';

/**
 * @typedef {{ value: string, swatch: string }} OwnedColor
 */

/**
 * @typedef {{ id: number, handle: string, title: string, url: string, image: string, colors: OwnedColor[], sizes: string[], overlayImages?: Array<{ c: string, u: string }>, labelImages?: Array<{ c: string, u: string }> }} OwnedProduct
 */

export class BadgeOnlyCustomizerTrigger extends Component {
  /** @type {AbortController | null} */
  #listenersAbortController = null;
  /** @type {OwnedProduct[]} */
  #ownedProducts = [];
  /** @type {Record<string, unknown> | null} */
  #parsedMappings = null;
  /** @type {OwnedProduct | null} */
  #selectedProduct = null;
  /** @type {string} */
  #selectedOwnedColor = '';
  /** @type {string} */
  #selectedOwnedSize = '';
  /** @type {string} */
  #mappedBadgeSize = '';
  /** @type {boolean} */
  #mappedSizeApplied = false;
  /** @type {string} */
  #customMessage = '';
  /** @type {string} */
  #secondaryText = '';
  /** @type {OwnedProduct[]} */
  #eligibleProducts = [];
  /** @type {HTMLElement | null} */
  #bottomPanel = null;
  /** @type {Map<string, Promise<HTMLImageElement>>} */
  #imageCache = new Map();
  /** @type {boolean | null} */
  #debugEnabled = null;
  /** @type {boolean} */
  #suppressNextVariantPickerPreview = false;
  /** @type {MutationObserver | null} */
  #variantPickerObserver = null;

  connectedCallback() {
    super.connectedCallback();
    this.#initialize().catch(() => {
      this.#renderError(this.dataset.addError || 'Unable to initialize badge customizer.');
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#listenersAbortController?.abort();
    this.#listenersAbortController = null;
    this.#cleanupVariantPickerObserver();
    this.#bottomPanel?.remove();
    this.#bottomPanel = null;
    this.#ownedProducts = [];
    this.#eligibleProducts = [];
    this.#removeLineItemPropertyInputs();
    this.#imageCache.clear();
  }

  async #initialize() {
    await this.#initializeState();

    this.#debug('Initialized state', {
      ownedProductsCount: this.#ownedProducts.length,
      eligibleProductsCount: this.#eligibleProducts.length,
      mappingsLoaded: Boolean(this.#parsedMappings),
    });

    if (this.#eligibleProducts.length === 0) {
      this.#renderError(this.dataset.noEligibleProductsLabel || 'No products are currently eligible for badge customization.');
      return;
    }

    this.#selectedProduct = this.#eligibleProducts[0] || null;
    this.#selectedOwnedColor = this.#resolveInitialOwnedColor(this.#selectedProduct);
    this.#selectedOwnedSize = this.#selectedProduct?.sizes?.[0] || '';
    this.#mappedBadgeSize = this.#resolveMappedBadgeSize(this.#selectedProduct, this.#selectedOwnedSize);

    this.#placeTopAboveVariantPicker();
    this.#mountBottomPanel();
    this.#renderTop();
    this.#renderBottom();
    this.#attachListeners();
    this.#observeVariantPicker();
    this.#hideVariantSizeSelector();
    this.#applyMappedSizeToVariantPicker();
    this.#syncInputDependentUI();
    this.#syncLineItemPropertiesToForm();
  }

  async #initializeState() {
    this.#ownedProducts = this.#parseOwnedProducts(this.dataset.ownedProducts || '[]');
    this.#parsedMappings = this.#parseJsonObject(this.dataset.sizeMappings || '{}');
    if (this.#ownedProducts.length === 0) {
      const collectionHandle = String(this.dataset.ownedProductsCollectionHandle || '').trim();
      const maxProducts = Number(this.dataset.maxProducts || '24');
      if (collectionHandle) {
        this.#ownedProducts = await this.#loadOwnedProductsFromCollection(collectionHandle, maxProducts);
      }
    }

    this.#eligibleProducts = this.#resolveEligibleProducts();
  }

  /**
   * @param {string} handle
   * @param {number} maxProducts
   * @returns {Promise<OwnedProduct[]>}
   */
  async #loadOwnedProductsFromCollection(handle, maxProducts) {
    const limit = Number.isFinite(maxProducts) ? Math.min(Math.max(maxProducts, 1), 250) : 24;
    const requestUrl = `/collections/${encodeURIComponent(handle)}/products.json?limit=${limit}`;

    try {
      const response = await fetch(requestUrl, {
        method: 'GET',
        headers: {
          Accept: 'application/json',
        },
      });

      if (!response.ok) return [];

      const payload = await response.json();
      const products = Array.isArray(payload?.products) ? payload.products : [];
      const normalizedProducts = [];

      for (const product of products) {
        if (!product || typeof product !== 'object') continue;

        const options = Array.isArray(product.options) ? product.options : [];
        const sizes = this.#extractProductOptionValues(options, ['size']);

        normalizedProducts.push({
          id: Number(product.id),
          handle: String(product.handle || ''),
          title: String(product.title || ''),
          url: `/products/${encodeURIComponent(String(product.handle || ''))}`,
          image: this.#resolveProductImage(product),
          colors: this.#extractProductOptionValues(options, ['color', 'colour']).map((value) => ({ value, swatch: value })),
          sizes,
          overlayImages: [],
          labelImages: [],
        });
      }

      return normalizedProducts.filter((item) => Number.isFinite(item.id) && item.handle && item.title);
    } catch {
      return [];
    }
  }

  #renderTop() {
    if (this.#eligibleProducts.length === 0) {
      this.#renderError(this.dataset.noEligibleProductsLabel || 'No products are currently eligible for badge customization.');
      return;
    }

    const productOptions = this.#eligibleProducts
      .map((product) => {
        const checked = this.#selectedProduct?.id === product.id ? 'checked' : '';
        const imageMarkup = product.image
          ? `<img class="badge-only-customizer__product-image" src="${this.#escapeHtml(product.image)}" alt="" loading="lazy">`
          : '';

        return `
          <label class="badge-only-customizer__option badge-only-customizer__product-option">
            <input type="radio" name="badge-owned-product" value="${product.id}" ${checked}>
            <span class="badge-only-customizer__option-content">
              ${imageMarkup}
              <span>${this.#escapeHtml(product.title)}</span>
            </span>
          </label>
        `;
      })
      .join('');

    const selectedProductTitle = this.#selectedProduct?.title || this.dataset.ownedProductStepLabel || 'Step 1: Select your product';
    const selectedProductImage = this.#selectedProduct?.image
      ? `<img class="badge-only-customizer__product-image" src="${this.#escapeHtml(this.#selectedProduct.image)}" alt="" loading="lazy">`
      : '';
    const selectedProductColors = this.#resolveSelectableOwnedColors(this.#selectedProduct);
    const colorOptions = selectedProductColors
      .map((colorObj) => {
        const color = colorObj.value;
        const swatch = colorObj.swatch;
        const checked = color === this.#selectedOwnedColor ? 'checked' : '';
        const checkedState = color === this.#selectedOwnedColor ? 'true' : 'false';
        const swatchBackground = this.#resolveOwnedColorSwatchBackground(swatch);
        const swatchClasses = swatchBackground ? 'swatch swatch--unscaled' : 'swatch swatch--unscaled swatch--unavailable';
        const swatchStyle = swatchBackground ? ` style="--swatch-background: ${this.#escapeHtml(swatchBackground)};"` : '';

        return `
          <label class="variant-option__button-label variant-option__button-label--has-swatch badge-only-customizer__variant-choice badge-only-customizer__color-choice">
            <input type="radio" name="badge-owned-color" value="${this.#escapeHtml(color)}" aria-label="${this.#escapeHtml(color)}" data-previous-checked="false" data-current-checked="${checkedState}" ${checked}>
            <span class="${swatchClasses}"${swatchStyle}></span>
            <span class="visually-hidden">${this.#escapeHtml(color)}</span>
          </label>
        `;
      })
      .join('');
    const selectedProductSizes = this.#selectedProduct?.sizes || [];
    const sizeFieldsetClasses = this.#resolveVariantSizeFieldsetClasses();
    const sizeOptions = selectedProductSizes
      .map((size) => {
        const checked = size === this.#selectedOwnedSize ? 'checked' : '';
        const checkedState = size === this.#selectedOwnedSize ? 'true' : 'false';
        return `
          <label class="variant-option__button-label badge-only-customizer__variant-choice">
            <input type="radio" name="badge-owned-size" value="${this.#escapeHtml(size)}" data-previous-checked="false" data-current-checked="${checkedState}" data-option-available="true" ${checked}>
            <span class="variant-option__button-label__pill" data-key="variant-option-pill"></span>
            <span class="variant-option__button-label__text">${this.#escapeHtml(size)}</span>
          </label>
        `;
      })
      .join('');
    const mappedSizeSummary = this.#mappedBadgeSize || 'Not selected';
    const sizeUnavailableLabel = this.dataset.sizeUnavailableLabel || 'No compatible size available for this selection.';
    const stepOneLabel = this.dataset.ownedProductStepLabel || 'Step 1: Select your product configuration';
    const selectColorLabel = this.dataset.selectColorLabel || 'Color:';
    const selectSizeLabel = this.dataset.selectSizeLabel || 'Size:';
    const mappedBadgeSizeLabel = this.dataset.mappedBadgeSizeLabel || 'Mapped badge size';

    this.innerHTML = `
      <div class="badge-only-customizer badge-only-customizer--top" data-badge-only-customizer>
        <div class="badge-only-customizer__step__container">
          <p class="badge-only-customizer__step-label product-badges__badge product-badges__badge--rectangle">${this.#escapeHtml(stepOneLabel)}</p>
          <div class="badge-only-customizer__step">
            <details class="badge-only-customizer__dropdown" ${this.#selectedProduct ? '' : 'open'}>
              <summary class="badge-only-customizer__dropdown-summary">
                <span class="badge-only-customizer__dropdown-summary-content">
                  ${selectedProductImage}
                  <span>${this.#escapeHtml(selectedProductTitle)}</span>
                </span>
              </summary>
              <div class="badge-only-customizer__dropdown-options badge-only-customizer__options">${productOptions}</div>
            </details>
            ${selectedProductColors.length > 0 ? `
              <p class="badge-only-customizer__help">${this.#escapeHtml(selectColorLabel)}</p>
              <fieldset class="variant-option variant-option--buttons variant-option--swatches badge-only-customizer__options" role="radiogroup" aria-label="${this.#escapeHtml(selectColorLabel)}">
                ${colorOptions}
              </fieldset>
            ` : ''}
            <p class="badge-only-customizer__help">${this.#escapeHtml(selectSizeLabel)}</p>
            <fieldset class="variant-option variant-option--buttons variant-option--equal-width-buttons badge-only-customizer__options ${this.#escapeHtml(sizeFieldsetClasses)}" role="radiogroup" aria-label="${this.#escapeHtml(selectSizeLabel)}">
              ${sizeOptions}
            </fieldset>
            <p class="badge-only-customizer__help">${this.#escapeHtml(mappedBadgeSizeLabel)}: ${this.#escapeHtml(mappedSizeSummary)}</p>
            <p class="badge-only-customizer__error ${this.#selectedOwnedSize && (!this.#mappedBadgeSize || !this.#mappedSizeApplied) ? '' : 'badge-only-customizer__hidden'}" data-badge-size-error>
              ${this.#escapeHtml(sizeUnavailableLabel)}
            </p>
          </div>
        </div>
      </div>
    `;
  }

  #renderBottom() {
    if (!(this.#bottomPanel instanceof HTMLElement)) return;

    const selectedOverlayImages = Array.isArray(this.#selectedProduct?.overlayImages)
      ? this.#selectedProduct.overlayImages
      : [];
    const selectedLabelImages = this.#resolveLabelImagesForPreview();
    const hasPreviewAssets = selectedOverlayImages.length > 0 || selectedLabelImages.length > 0;

    const stepTwoLabel = this.dataset.customizeStepLabel || 'Step 2: Customize your badge';
    const previewUnavailableLabel = this.dataset.previewUnavailableLabel || 'Preview images are not configured for this product.';

    this.#bottomPanel.innerHTML = `
      <div class="badge-only-customizer badge-only-customizer--bottom" data-badge-bottom-panel>
        <div class="badge-only-customizer__step__container">
          <p class="badge-only-customizer__step-label product-badges__badge product-badges__badge--rectangle">${this.#escapeHtml(stepTwoLabel)}</p>
          <div class="badge-only-customizer__step">
            <div class="badge-only-customizer__preview" data-badge-preview>
              <div class="badge-only-customizer__preview-frame">
                <canvas class="badge-only-customizer__preview-canvas" data-badge-preview-canvas aria-hidden="true"></canvas>
              </div>
              <p class="badge-only-customizer__preview-status ${hasPreviewAssets ? 'badge-only-customizer__hidden' : ''}" data-badge-preview-status>
                ${this.#escapeHtml(previewUnavailableLabel)}
              </p>
            </div>
            <label class="badge-only-customizer__field">
              <span>${this.#escapeHtml(this.dataset.customMessageLabel || 'Custom Message')}</span>
              <input
                type="text"
                class="field__input"
                data-badge-custom-message
                maxlength="16"
                autocomplete="off"
                value="${this.#escapeHtml(this.#customMessage)}"
              >
            </label>
            <label class="badge-only-customizer__field">
              <span>${this.#escapeHtml(this.dataset.secondaryTextLabel || 'Secondary Text (optional)')}</span>
              <input
                type="text"
                class="field__input"
                data-badge-secondary-text
                maxlength="16"
                autocomplete="off"
                value="${this.#escapeHtml(this.#secondaryText)}"
              >
            </label>
            <p class="badge-only-customizer__error ${this.#customMessage ? 'badge-only-customizer__hidden' : ''}" data-badge-custom-message-error>
              ${this.#escapeHtml(this.dataset.customMessageRequired || 'Custom message is required.')}
            </p>
          </div>
        </div>
      </div>
    `;

    this.#renderPreviewFromState();
    this.#syncLineItemPropertiesToForm();
  }

  #attachListeners() {
    this.#listenersAbortController?.abort();
    this.#listenersAbortController = new AbortController();
    const signal = this.#listenersAbortController.signal;

    this.addEventListener(
      'change',
      (event) => {
        const target = event.target;
        if (!(target instanceof HTMLInputElement)) return;

        if (target.name === 'badge-owned-product') {
          this.#handleOwnedProductChange(target.value);
          return;
        }

        if (target.name === 'badge-owned-size') {
          this.#selectedOwnedSize = target.value || '';
          this.#mappedBadgeSize = this.#resolveMappedBadgeSize(this.#selectedProduct, this.#selectedOwnedSize);
          this.#mappedSizeApplied = false;
          this.#renderTop();
          this.#applyMappedSizeToVariantPicker();
          this.#syncInputDependentUI();
          this.#renderPreviewFromState();
          this.#syncLineItemPropertiesToForm();
          return;
        }

        if (target.name === 'badge-owned-color') {
          this.#selectedOwnedColor = target.value || '';
          this.#syncInputDependentUI();
          this.#renderPreviewFromState();
          this.#syncLineItemPropertiesToForm();
        }
      },
      { signal }
    );

    document.addEventListener(
      'click',
      (event) => {
        const dropdown = this.querySelector('.badge-only-customizer__dropdown');
        if (!(dropdown instanceof HTMLDetailsElement) || !dropdown.open) return;

        if (event.target instanceof Node && dropdown.contains(event.target)) return;

        dropdown.open = false;
      },
      { signal }
    );

    this.#bottomPanel?.addEventListener(
      'input',
      (event) => {
        const target = event.target;
        if (!(target instanceof HTMLInputElement)) return;

        if (target.dataset.badgeCustomMessage !== undefined) {
          this.#customMessage = target.value.trim();
          this.#syncInputDependentUI();
          this.#renderPreviewFromState();
          this.#syncLineItemPropertiesToForm();
          return;
        }

        if (target.dataset.badgeSecondaryText !== undefined) {
          this.#secondaryText = target.value.trim();
          this.#renderPreviewFromState();
          this.#syncLineItemPropertiesToForm();
        }
      },
      { signal }
    );

    const variantPicker = this.#resolveVariantPicker();
    variantPicker?.addEventListener(
      'change',
      () => {
        this.#hideVariantSizeSelector();
        if (this.#suppressNextVariantPickerPreview) {
          this.#suppressNextVariantPickerPreview = false;
          return;
        }
        this.#renderPreviewFromState();
      },
      { signal }
    );

    const productForm = this.#resolveProductForm();
    productForm?.addEventListener(
      'submit',
      (event) => {
        if (this.#canSubmit()) return;
        event.preventDefault();
        event.stopPropagation();
        this.#syncInputDependentUI();
      },
      { signal }
    );
  }

  /** @param {string} productId */
  #handleOwnedProductChange(productId) {
    const numericId = Number(productId);
    const selected = this.#eligibleProducts.find((item) => item.id === numericId) || null;

    this.#selectedProduct = selected;
    this.#selectedOwnedColor = this.#resolveInitialOwnedColor(this.#selectedProduct);
    this.#selectedOwnedSize = this.#selectedProduct?.sizes?.[0] || '';
    this.#mappedBadgeSize = this.#resolveMappedBadgeSize(this.#selectedProduct, this.#selectedOwnedSize);
    this.#mappedSizeApplied = false;

    this.#renderTop();
    this.#applyMappedSizeToVariantPicker();
    this.#syncInputDependentUI();
    this.#renderPreviewFromState();
    this.#syncLineItemPropertiesToForm();

    const dropdown = this.querySelector('.badge-only-customizer__dropdown');
    if (dropdown instanceof HTMLDetailsElement) {
      dropdown.open = false;
    }
  }

  #syncInputDependentUI() {
    const sizeError = this.querySelector('[data-badge-size-error]');
    if (sizeError instanceof HTMLElement) {
      const showSizeError = Boolean(this.#selectedOwnedSize) && (!this.#mappedBadgeSize || !this.#mappedSizeApplied);
      sizeError.classList.toggle('badge-only-customizer__hidden', !showSizeError);
    }

    const customMessageError = this.#bottomPanel?.querySelector('[data-badge-custom-message-error]');
    if (customMessageError instanceof HTMLElement) {
      customMessageError.classList.toggle('badge-only-customizer__hidden', Boolean(this.#customMessage));
    }

    const submitButton = this.#resolveSubmitButton();
    if (submitButton instanceof HTMLButtonElement) {
      submitButton.disabled = !this.#canSubmit();
      submitButton.setAttribute('aria-disabled', submitButton.disabled ? 'true' : 'false');
    }
  }

  #placeTopAboveVariantPicker() {
    const variantPicker = this.#resolveVariantPicker();
    if (!(variantPicker instanceof HTMLElement)) return;

    const parent = variantPicker.parentElement;
    if (!parent) return;

    if (this.nextElementSibling === variantPicker) return;
    parent.insertBefore(this, variantPicker);
  }

  #mountBottomPanel() {
    const variantPicker = this.#resolveVariantPicker();
    if (!(variantPicker instanceof HTMLElement)) return;

    if (!(this.#bottomPanel instanceof HTMLElement)) {
      this.#bottomPanel = document.createElement('div');
      this.#bottomPanel.className = 'badge-only-customizer-mount';
    }

    variantPicker.insertAdjacentElement('afterend', this.#bottomPanel);
  }

  #hideVariantSizeSelector() {
    const variantPicker = this.#resolveVariantPicker();
    if (!(variantPicker instanceof HTMLElement)) return;

    /** @type {HTMLFieldSetElement[]} */
    const sizeFieldsets = [
      ...variantPicker.querySelectorAll('fieldset[data-fieldset-option-name="size" i]'),
      ...variantPicker.querySelectorAll('fieldset[data-option-name="size" i]'),
    ].filter((fieldset) => fieldset instanceof HTMLFieldSetElement);

    const fallbackSizeFieldset = this.#resolveVariantSizeFieldset();
    if (
      fallbackSizeFieldset instanceof HTMLFieldSetElement
      && !sizeFieldsets.includes(fallbackSizeFieldset)
    ) {
      sizeFieldsets.push(fallbackSizeFieldset);
    }

    for (const sizeFieldset of sizeFieldsets) {
      sizeFieldset.classList.add('badge-only-customizer__variant-size-hidden');
      sizeFieldset.setAttribute('aria-hidden', 'true');
      sizeFieldset.hidden = true;
    }
  }

  #observeVariantPicker() {
    this.#cleanupVariantPickerObserver();

    const variantPicker = this.#resolveVariantPicker();
    if (!(variantPicker instanceof HTMLElement)) return;

    this.#variantPickerObserver = new MutationObserver(() => {
      this.#hideVariantSizeSelector();
    });

    this.#variantPickerObserver.observe(variantPicker, {
      childList: true,
      subtree: true,
    });
  }

  #cleanupVariantPickerObserver() {
    this.#variantPickerObserver?.disconnect();
    this.#variantPickerObserver = null;
  }

  #applyMappedSizeToVariantPicker() {
    if (!this.#mappedBadgeSize) {
      this.#mappedSizeApplied = false;
      this.#debug('Skipping mapped size apply: no mapped size', {
        selectedOwnedSize: this.#selectedOwnedSize,
      });
      return;
    }

    const sizeFieldset = this.#resolveVariantSizeFieldset();
    if (!(sizeFieldset instanceof HTMLFieldSetElement)) {
      this.#mappedSizeApplied = false;
      this.#debug('Skipping mapped size apply: size fieldset not found', {
        mappedBadgeSize: this.#mappedBadgeSize,
      });
      return;
    }

    const radios = [...sizeFieldset.querySelectorAll('input[type="radio"]')];
    const target = radios.find(
      (radio) => radio instanceof HTMLInputElement && this.#normalizeValue(radio.value) === this.#normalizeValue(this.#mappedBadgeSize)
    );
    if (!(target instanceof HTMLInputElement)) {
      this.#mappedSizeApplied = false;
      this.#debug('Mapped size not found in variant picker', {
        mappedBadgeSize: this.#mappedBadgeSize,
        availableSizes: radios
          .filter((radio) => radio instanceof HTMLInputElement)
          .map((radio) => radio.value),
      });
      return;
    }

    this.#mappedSizeApplied = true;

    if (target.checked) {
      this.#debug('Mapped size already selected; no change dispatch', {
        mappedBadgeSize: this.#mappedBadgeSize,
      });
      return;
    }

    this.#debug('Applying mapped size to variant picker', {
      mappedBadgeSize: this.#mappedBadgeSize,
      targetValue: target.value,
    });

    this.#suppressNextVariantPickerPreview = true;
    target.checked = true;
    target.setAttribute('data-current-checked', 'true');
    target.dispatchEvent(new Event('change', { bubbles: true }));
    this.#syncInputDependentUI();
  }

  /** @returns {OwnedProduct[]} */
  #resolveEligibleProducts() {
    const eligible = [];
    for (const product of this.#ownedProducts) {
      const mapping = this.#resolveProductMappings(product);
      if (!mapping) continue;
      if (!Array.isArray(product.sizes) || product.sizes.length === 0) continue;

      const hasCompatibleSize = product.sizes.some((size) => {
        const mapped = this.#resolveMappedBadgeSize(product, size);
        return Boolean(mapped);
      });

      if (hasCompatibleSize) eligible.push(product);
    }

    return eligible;
  }

  /** @param {Record<string, unknown>} product */
  #resolveProductImage(product) {
    const normalizedProduct = /** @type {{ image?: { src?: string }, featured_image?: string | { src?: string }, images?: Array<{ src?: string }> }} */ (product);
    const featuredImage =
      typeof normalizedProduct.featured_image === 'string'
        ? normalizedProduct.featured_image
        : normalizedProduct.featured_image?.src;

    const imageSrc = String(normalizedProduct.image?.src || featuredImage || normalizedProduct.images?.[0]?.src || '').trim();
    if (!imageSrc) return '';

    if (imageSrc.includes('?')) {
      return `${imageSrc}&width=96`;
    }

    return `${imageSrc}?width=96`;
  }

  /** @returns {boolean} */
  #canSubmit() {
    return Boolean(this.#selectedProduct && this.#selectedOwnedSize && this.#mappedBadgeSize && this.#mappedSizeApplied && this.#customMessage);
  }

  /**
   * @param {Array<{ name?: string, values?: unknown[] }>} options
   * @param {string[]} matchingNames
   * @returns {string[]}
   */
  #extractProductOptionValues(options, matchingNames) {
    const option = options.find((entry) => {
      const optionName = String(entry?.name || '').trim().toLowerCase();
      return matchingNames.some((name) => optionName.includes(name));
    });

    if (!Array.isArray(option?.values)) return [];

    return option.values
      .map((value) => String(value || '').trim())
      .filter((value, index, values) => value && values.indexOf(value) === index);
  }

  /** @param {OwnedProduct | null} product */
  #resolveSelectableOwnedColors(product) {
    if (!product) return [];

    const directColors = Array.isArray(product.colors)
      ? product.colors
          .map((item) => {
            if (item && typeof item === 'object' && 'value' in item) {
              const normalizedItem = /** @type {{ value?: unknown, swatch?: unknown }} */ (item);
              return {
                value: String(normalizedItem.value || '').trim(),
                swatch: String(normalizedItem.swatch || normalizedItem.value || '').trim(),
              };
            }
            const value = String(item || '').trim();
            return { value, swatch: value };
          })
          .filter((item, index, items) => item.value && items.findIndex((i) => i.value === item.value) === index)
      : [];

    if (directColors.length > 0) return directColors;

    return Array.isArray(product.overlayImages)
      ? product.overlayImages
          .map((image) => {
            const value = String(image?.c || '').trim();
            return { value, swatch: value };
          })
          .filter((item, index, items) => item.value && items.findIndex((i) => i.value === item.value) === index)
      : [];
  }

  /** @param {OwnedProduct | null} product */
  #resolveInitialOwnedColor(product) {
    return this.#resolveSelectableOwnedColors(product)[0]?.value || '';
  }

  /** @param {string} colorOrSwatch */
  #resolveOwnedColorSwatchBackground(colorOrSwatch) {
    const value = String(colorOrSwatch || '').trim();
    if (!value) return '';

    if (value.startsWith('url(') || value.startsWith('rgb(')) return value;

    const hexPattern = /^#(?:[0-9a-f]{3}|[0-9a-f]{6}|[0-9a-f]{8})$/iu;
    if (hexPattern.test(value)) return value;

    const functionalColorPattern = /^(?:rgba|hsl|hsla|oklch|oklab|lab|lch|hwb)\(.+\)$/iu;
    if (functionalColorPattern.test(value)) return value;

    const keywordPattern = /^[a-z]+$/iu;
    if (keywordPattern.test(value)) return value.toLowerCase();

    return '';
  }

  #resolveVariantPicker() {
    const section = this.closest('.shopify-section') || document;
    return section.querySelector('variant-picker[data-template-product-match="true"]') || section.querySelector('variant-picker');
  }

  #resolveVariantSizeFieldset() {
    const picker = this.#resolveVariantPicker();
    if (!(picker instanceof HTMLElement)) return null;

    const exact = picker.querySelector('fieldset[data-fieldset-option-name="size"]');
    if (exact instanceof HTMLFieldSetElement) return exact;

    const fieldsets = [...picker.querySelectorAll('fieldset.variant-option, fieldset.variant-option--buttons')];
    return fieldsets.find((fieldset) => {
      if (!(fieldset instanceof HTMLFieldSetElement)) return false;

      const optionName =
        fieldset.getAttribute('data-fieldset-option-name')
        || fieldset.getAttribute('data-option-name')
        || fieldset.querySelector('legend')?.textContent
        || '';

      return this.#normalizeValue(optionName).includes('size');
    }) || null;
  }

  #resolveVariantSizeFieldsetClasses() {
    const sizeFieldset = this.#resolveVariantSizeFieldset();
    if (!(sizeFieldset instanceof HTMLFieldSetElement)) return '';

    const filtered = [...sizeFieldset.classList].filter((className) => className !== 'badge-only-customizer__variant-size-hidden');
    return filtered.join(' ');
  }

  #resolveProductForm() {
    const section = this.closest('.shopify-section') || document;
    return section.querySelector('product-form-component form[data-type="add-to-cart-form"], product-form-component form');
  }

  #resolveProductFormId() {
    const form = this.#resolveProductForm();
    if (!(form instanceof HTMLFormElement)) return '';
    if (form.id) return form.id;

    form.id = `badge-only-customizer-form-${this.dataset.sectionId || 'main'}`;
    return form.id;
  }

  #resolveSubmitButton() {
    const productForm = this.#resolveProductForm();
    if (!(productForm instanceof HTMLFormElement)) return null;
    return productForm.querySelector('button[type="submit"]');
  }

  #syncLineItemPropertiesToForm() {
    const form = this.#resolveProductForm();
    if (!(form instanceof HTMLFormElement)) return;

    this.#setFormPropertyInput(form, 'properties[Custom Message]', this.#customMessage);
    this.#setFormPropertyInput(form, 'properties[Secondary Text]', this.#secondaryText);
    this.#setFormPropertyInput(form, 'properties[Applies To Product]', this.#selectedProduct?.title || '');
    // this.#setFormPropertyInput(form, 'properties[Applies To Color]', this.#selectedOwnedColor);
    this.#setFormPropertyInput(form, 'properties[Applies To Size]', this.#selectedOwnedSize);
  }

  #removeLineItemPropertyInputs() {
    const form = this.#resolveProductForm();
    if (!(form instanceof HTMLFormElement)) return;

    const managedInputs = form.querySelectorAll('input[data-badge-line-property="true"]');
    for (const input of managedInputs) {
      input.remove();
    }
  }

  /**
   * @param {HTMLFormElement} form
   * @param {string} name
   * @param {string} value
   */
  #setFormPropertyInput(form, name, value) {
    const selectorName = name.replace(/"/g, '\\"');
    let input = /** @type {HTMLInputElement | null} */ (
      form.querySelector(`input[data-badge-line-property="true"][name="${selectorName}"]`)
    );
    if (!(input instanceof HTMLInputElement)) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      input.dataset.badgeLineProperty = 'true';
      form.append(input);
    }

    input.value = String(value || '');
  }

  #renderPreviewFromState() {
    const preview = this.#bottomPanel?.querySelector('[data-badge-preview]');
    if (!(preview instanceof HTMLElement)) return;

    const canvas = preview.querySelector('[data-badge-preview-canvas]');
    const status = preview.querySelector('[data-badge-preview-status]');
    if (!(canvas instanceof HTMLCanvasElement)) return;
    if (!(status instanceof HTMLElement)) return;

    const badgeColor = this.#resolveSelectedBadgeColor() || '';

    const selectedOverlayImages = Array.isArray(this.#selectedProduct?.overlayImages)
      ? this.#selectedProduct.overlayImages
      : [];
    const selectedLabelImages = this.#resolveLabelImagesForPreview();

    const overlayColorCandidates = this.#resolveOverlayColorCandidates(this.#selectedOwnedColor, badgeColor);
    let overlayUrl = '';
    for (const candidate of overlayColorCandidates) {
      overlayUrl = this.#matchImageByColor(selectedOverlayImages, candidate);
      if (overlayUrl) break;
    }
    if (!overlayUrl) {
      status.classList.remove('badge-only-customizer__hidden');
      status.textContent = this.dataset.previewUnavailableLabel || 'Preview images are not configured for this product.';
      const context = canvas.getContext('2d');
      if (context instanceof CanvasRenderingContext2D) {
        context.clearRect(0, 0, canvas.width, canvas.height);
      }
      return;
    }

    const labelColorKey = [badgeColor, this.#mappedBadgeSize].filter(Boolean).join(' ');
    const labelUrl =
      this.#matchImageByColor(selectedLabelImages, labelColorKey)
      || this.#matchImageByColor(selectedLabelImages, badgeColor)
      || this.#matchImageByColor(selectedLabelImages, this.#mappedBadgeSize)
      || (selectedLabelImages[0]?.u || '');

    this.#debug('Preview image resolution', {
      selectedProduct: this.#selectedProduct?.title || null,
      selectedOwnedColor: this.#selectedOwnedColor,
      selectedOwnedSize: this.#selectedOwnedSize,
      mappedBadgeSize: this.#mappedBadgeSize,
      badgeColor,
      overlayColorCandidates,
      overlayImagesCount: selectedOverlayImages.length,
      labelImagesCount: selectedLabelImages.length,
      overlayUrl,
      labelColorKey,
      labelUrl,
    });

    status.classList.add('badge-only-customizer__hidden');

    const matchedOverlayColor = this.#resolveMatchedOverlayColorKey(
      selectedOverlayImages,
      overlayColorCandidates
    );

    this.#renderBadgeCanvas(
      canvas,
      overlayUrl,
      labelUrl || null,
      this.#customMessage,
      this.#secondaryText,
      matchedOverlayColor
    ).then((rendered) => {
      if (rendered === false) {
        status.classList.remove('badge-only-customizer__hidden');
        status.textContent =
          this.dataset.previewUnavailableLabel || 'Preview images are not configured for this product.';
      }
    });
  }

  /**
   * @param {HTMLCanvasElement} canvas
   * @param {string} overlayUrl
   * @param {string | null} labelUrl
   * @param {string} primaryText
   * @param {string} secondaryText
   * @param {string} [overlayColorKey]
   * @returns {Promise<boolean>}
   */
  async #renderBadgeCanvas(canvas, overlayUrl, labelUrl, primaryText, secondaryText, overlayColorKey = '') {
    const [bgImg, labelImg] = await Promise.all([
      loadBadgeImage(overlayUrl, this.#imageCache),
      labelUrl ? loadBadgeImage(labelUrl, this.#imageCache) : Promise.resolve(null),
    ]);

    if (!(bgImg instanceof HTMLImageElement)) {
      this.#debug('Badge canvas background failed to load', { overlayUrl });
      return false;
    }

    canvas.width = bgImg.naturalWidth;
    canvas.height = bgImg.naturalHeight;

    const ctx = canvas.getContext('2d');
    if (!(ctx instanceof CanvasRenderingContext2D)) return false;

    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(bgImg, 0, 0);

    if (labelImg instanceof HTMLImageElement) {
      const anchor = resolveLabelAnchor(
        ctx,
        canvas.width,
        canvas.height,
        this.#resolveLabelAnchorConfig(this.#selectedProduct, overlayColorKey)
      );

      const labelScale = this.#resolveLabelScale();
      const labelW = labelImg.naturalWidth * labelScale;
      const labelH = labelImg.naturalHeight * labelScale;
      const startX = anchor.x - labelW / 2.1;
      const startY = anchor.y - labelH / 2.6;

      ctx.drawImage(labelImg, startX, startY, labelW, labelH);

      const text1 = String(primaryText || '').trim().toUpperCase();
      const text2 = String(secondaryText || '').trim().toUpperCase();
      if (!text1 && !text2) return true;

      const fontSize = calcLabelFontSize(labelW, text1, text2);
      ctx.font = `bold ${fontSize}px Arial, sans-serif`;
      ctx.fillStyle = '#ffffff';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';

      const textX = startX + labelW / 2;
      if (text1 && text2) {
        ctx.fillText(text1, textX, startY + labelH * 0.34);
        ctx.fillText(text2, textX, startY + labelH * 0.64);
      } else {
        ctx.fillText(text1 || text2, textX, startY + labelH * 0.5);
      }
      return true;
    }

    const fallbackText1 = String(primaryText || '').trim().toUpperCase();
    const fallbackText2 = String(secondaryText || '').trim().toUpperCase();
    if (!fallbackText1 && !fallbackText2) return true;

    const fallbackFont = Math.max(16, Math.round(canvas.width * 0.06));
    ctx.font = `bold ${fallbackFont}px Arial, sans-serif`;
    ctx.fillStyle = '#ffffff';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.shadowColor = 'rgb(0 0 0 / 35%)';
    ctx.shadowBlur = 4;
    const centerX = Math.round(canvas.width * 0.5);
    if (fallbackText1 && fallbackText2) {
      ctx.fillText(fallbackText1, centerX, Math.round(canvas.height * 0.46));
      ctx.fillText(fallbackText2, centerX, Math.round(canvas.height * 0.56));
    } else {
      ctx.fillText(fallbackText1 || fallbackText2, centerX, Math.round(canvas.height * 0.51));
    }

    return true;
  }

  /**
   * @param {OwnedProduct | null} [product]
   * @param {string} [overlayColor]
   * @returns {import('./badge-canvas.js').LabelAnchorConfig | null}
   */
  #resolveLabelAnchorConfig(product = this.#selectedProduct, overlayColor = '') {
    if (!product) return null;

    return resolveMappingLabelAnchor(this.#parsedMappings, {
      productHandle: product.handle,
      productId: product.id,
      overlayColor,
    });
  }

  /** @returns {Array<{ c: string, u: string }>} */
  #resolveLabelImagesForPreview() {
    const ownedLabelImages = Array.isArray(this.#selectedProduct?.labelImages)
      ? this.#selectedProduct.labelImages
      : [];

    if (ownedLabelImages.length > 0) return ownedLabelImages;

    return this.#parseBadgeImageData(this.dataset.labelImages || '[]');
  }

  /**
   * @param {OwnedProduct | null} [product]
   * @returns {number}
   */
  #resolveLabelScale(product = this.#selectedProduct) {
    if (!product || !this.#parsedMappings) return 1;

    /** @param {unknown} value */
    const clampScale = (value) => {
      const num = Number(value);
      if (!Number.isFinite(num)) return 1;
      return Math.min(1.5, Math.max(0.3, num));
    };

    const byHandle = /** @type {Record<string, { label_scale?: unknown }>} */ (this.#parsedMappings.by_handle || {});
    const handleConfig = byHandle[product.handle];
    if (byHandle && typeof byHandle === 'object' && handleConfig && typeof handleConfig === 'object') {
      const { label_scale: labelScale } = handleConfig;
      if (labelScale !== undefined && labelScale !== null) {
        return clampScale(labelScale);
      }
    }

    const byProduct = /** @type {Record<string, { label_scale?: unknown }>} */ (this.#parsedMappings.by_product || {});
    const productKey = String(product.id);
    const productConfig = byProduct[productKey] || byProduct[Number(productKey)];
    if (byProduct && typeof byProduct === 'object' && productConfig && typeof productConfig === 'object') {
      const { label_scale: labelScale } = productConfig;
      if (labelScale !== undefined && labelScale !== null) {
        return clampScale(labelScale);
      }
    }

    return 1;
  }

  /**
   * @param {Array<{ c: string, u: string }>} images
   * @param {string[]} candidates
   * @returns {string}
   */
  #resolveMatchedOverlayColorKey(images, candidates) {
    if (!Array.isArray(images) || images.length === 0) return '';

    for (const candidate of candidates) {
      const url = this.#matchImageByColor(images, candidate);
      if (!url) continue;
      const match = images.find((image) => image.u === url);
      if (match?.c) return match.c;
    }

    return candidates[0] || '';
  }

  /** @param {OwnedProduct | null} product */
  #resolveProductMappings(product) {
    if (!product || !this.#parsedMappings) return null;

    const byHandle = /** @type {Record<string, { size_mappings?: Record<string, unknown> }>} */ (this.#parsedMappings.by_handle || {});
    const handleConfig = byHandle[product.handle];
    if (byHandle && typeof byHandle === 'object' && handleConfig?.size_mappings) {
      return handleConfig.size_mappings;
    }

    const byProduct = /** @type {Record<string, { size_mappings?: Record<string, unknown> }>} */ (this.#parsedMappings.by_product || {});
    const productKey = String(product.id);
    if (byProduct && typeof byProduct === 'object' && byProduct[productKey]?.size_mappings) {
      return byProduct[productKey].size_mappings;
    }

    return null;
  }

  /**
   * @param {OwnedProduct | null} product
   * @param {string} ownedSize
   * @returns {string}
   */
  #resolveMappedBadgeSize(product, ownedSize) {
    const mapping = this.#resolveProductMappings(product);
    if (!mapping || typeof mapping !== 'object') return '';

    const normalizedSource = this.#normalizeValue(ownedSize);

    for (const [sourceSize, targetSize] of Object.entries(mapping)) {
      if (this.#normalizeValue(sourceSize) !== normalizedSource) continue;
      if (Array.isArray(targetSize)) {
        return String(targetSize[0] || '');
      }
      return String(targetSize || '');
    }

    return '';
  }

  #resolveSelectedBadgeColor() {
    const picker = this.#resolveVariantPicker();
    if (!(picker instanceof HTMLElement)) return '';

    const fieldsets = [...picker.querySelectorAll('fieldset.variant-option, fieldset.variant-option--buttons')];
    const colorFieldset = fieldsets.find((fieldset) => {
      if (!(fieldset instanceof HTMLFieldSetElement)) return false;

      const optionName =
        fieldset.getAttribute('data-fieldset-option-name')
        || fieldset.getAttribute('data-option-name')
        || fieldset.getAttribute('option')
        ||
        fieldset.querySelector('legend')?.textContent
        || '';

      const normalized = this.#normalizeValue(optionName);
      return normalized.includes('color') || normalized.includes('colour');
    });

    if (!(colorFieldset instanceof HTMLFieldSetElement)) return '';

    const selectedRadio = colorFieldset.querySelector('input[type="radio"]:checked');
    if (selectedRadio instanceof HTMLInputElement) {
      const radioValue = String(selectedRadio.value || '').trim();
      if (radioValue) return radioValue;

      const radioLabel = selectedRadio.closest('label');
      const radioLabelText = radioLabel?.textContent?.trim();
      if (radioLabelText) return radioLabelText;
    }

    const selectedOption = colorFieldset.querySelector('select');
    if (selectedOption instanceof HTMLSelectElement) {
      return String(selectedOption.value || '').trim();
    }

    return '';
  }

  /**
   * @param {Array<{ c: string, u: string }>} images
   * @param {string} colorName
   */
  #matchImageByColor(images, colorName) {
    if (!Array.isArray(images) || images.length === 0 || !colorName) return '';

    const needle = this.#normalizeValue(colorName);
    if (!needle) return '';

    /** @param {string | null | undefined} value */
    const tokenize = (value) =>
      String(value || '')
        .toLowerCase()
        .split(/[^a-z0-9]+/gu)
        .map((part) => part.trim())
        .filter((part) => part.length >= 3);

    const needleTokens = new Set(tokenize(colorName));

    const normalizedImages = images.map((image) => ({
      ...image,
      normalizedColor: this.#normalizeValue(image.c),
      tokens: tokenize(image.c),
    }));

    const exact = normalizedImages.find((image) => image.normalizedColor === needle);
    if (exact?.u) return exact.u;

    const prefix = normalizedImages.find(
      (image) => image.normalizedColor.startsWith(needle) || needle.startsWith(image.normalizedColor)
    );
    if (prefix?.u) return prefix.u;

    const includes = normalizedImages.find(
      (image) => image.normalizedColor.includes(needle) || needle.includes(image.normalizedColor)
    );
    if (includes?.u) return includes.u;

    const tokenMatch = normalizedImages.find((image) => {
      if (!image.tokens.length || needleTokens.size === 0) return false;
      return image.tokens.some((token) => needleTokens.has(token));
    });
    return tokenMatch?.u || '';
  }

  /**
   * @param {string} ownedProductColor
   * @param {string} badgeColor
   */
  #resolveOverlayColorCandidates(ownedProductColor, badgeColor) {
    const values = [
      ownedProductColor,
      badgeColor,
      this.#selectedProduct?.title || '',
      this.#selectedProduct?.handle ? this.#selectedProduct.handle.replace(/[-_]+/gu, ' ') : '',
    ];

    /** @type {string[]} */
    const uniqueValues = [];
    for (const value of values) {
      const normalizedValue = this.#normalizeValue(value);
      if (!normalizedValue) continue;
      if (uniqueValues.some((entry) => this.#normalizeValue(entry) === normalizedValue)) continue;
      uniqueValues.push(value);
    }

    return uniqueValues;
  }

  /** @param {string} raw */
  #parseJsonArray(raw) {
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  /** @param {string} raw */
  #parseOwnedProducts(raw) {
    const attributeValue = this.getAttribute('data-owned-products') || '';
    const candidates = [
      String(raw || ''),
      String(attributeValue),
    ];

    if (this.#isDebugEnabled()) {
      this.#debug('parseOwnedProducts started', {
        rawParamLength: String(raw || '').length,
        attributeLength: attributeValue.length,
        attributePrefix: attributeValue.slice(0, 100),
      });
    }

    for (const candidate of candidates) {
      const parsed = this.#parseJsonArrayLenient(candidate);
      if (!Array.isArray(parsed) || parsed.length === 0) continue;

      const normalized = [];
      for (const item of parsed) {
        if (!item || typeof item !== 'object') continue;
        const product = /** @type {{ id?: number | string, handle?: string, title?: string, url?: string, image?: string, colors?: unknown, sizes?: unknown, overlayImages?: unknown, labelImages?: unknown }} */ (item);

        const normalizedProduct = {
          id: Number(product.id),
          handle: String(product.handle || '').trim(),
          title: String(product.title || '').trim(),
          url: String(product.url || '').trim(),
          image: String(product.image || '').trim(),
          colors: this.#normalizeOwnedColors(product.colors),
          sizes: this.#normalizeStringArray(product.sizes),
          overlayImages: this.#normalizeBadgeImages(product.overlayImages),
          labelImages: this.#normalizeBadgeImages(product.labelImages),
        };

        if (!Number.isFinite(normalizedProduct.id) || !normalizedProduct.handle || !normalizedProduct.title) continue;
        normalized.push(normalizedProduct);

        this.#debug('Parsed product', {
          handle: normalizedProduct.handle,
          title: normalizedProduct.title,
          rawOverlayImages: product.overlayImages,
          normalizedOverlayImagesCount: normalizedProduct.overlayImages.length,
          rawLabelImages: product.labelImages,
          normalizedLabelImagesCount: normalizedProduct.labelImages.length,
        });
      }

      if (normalized.length > 0) {
        return normalized;
      }
    }

    return [];
  }

  /** @param {string} raw */
  #parseJsonArrayLenient(raw) {
    const value = String(raw || '').trim();
    if (!value) return [];

    // Try to find the exact character where JSON parsing fails
    const findJsonBreak = () => {
      for (let i = 1; i < value.length; i += 1) {
        try {
          JSON.parse(value.slice(0, i));
        } catch {
          const charCode = value.charCodeAt(i - 1);
          const char = value[i - 1];
          return { position: i - 1, char, charCode, context: value.slice(Math.max(0, i - 20), i + 20) };
        }
      }
      return null;
    };

    const decodedCandidates = [
      value,
      this.#decodeHtmlEntities(value),
      value.replace(/&quot;/g, '"').replace(/&#39;/g, "'").replace(/&amp;/g, '&'),
    ];

    let candidateIndex = 0;
    for (const candidate of decodedCandidates) {
      let current = candidate;
      for (let attempt = 0; attempt < 3; attempt += 1) {
        try {
          const parsed = JSON.parse(current);
          if (Array.isArray(parsed)) {
            if (this.#isDebugEnabled()) {
              this.#debug('parseJsonArrayLenient succeeded', {
                candidateIndex,
                attemptNumber: attempt,
                parsedLength: parsed.length,
                originalLength: value.length,
              });
            }
            return parsed;
          }
          if (typeof parsed === 'string') {
            current = parsed;
            continue;
          }
          break;
        } catch (err) {
          if (this.#isDebugEnabled() && candidateIndex === 0 && attempt === 0) {
            const breakInfo = findJsonBreak();
            this.#debug('parseJsonArrayLenient error detail', {
              candidateIndex,
              attemptNumber: attempt,
              error: String(err),
              breakAt: breakInfo,
              candidatePrefix: candidate.slice(0, 150),
            });
          }
          break;
        }
      }
      candidateIndex += 1;
    }

    if (this.#isDebugEnabled()) {
      this.#debug('parseJsonArrayLenient failed all candidates', {
        inputLength: value.length,
        inputPrefix: value.slice(0, 100),
      });
    }

    return [];
  }

  /** @param {unknown} value */
  #normalizeStringArray(value) {
    if (Array.isArray(value)) {
      return value
        .map((entry) => String(entry || '').trim())
        .filter((entry) => entry);
    }

    if (typeof value === 'string') {
      const parsed = this.#parseJsonArrayLenient(value);
      if (Array.isArray(parsed) && parsed.length > 0) {
        return parsed
          .map((entry) => String(entry || '').trim())
          .filter((entry) => entry);
      }

      const tokenMatches = value.match(/[A-Za-z0-9]+/gu) || [];
      return tokenMatches
        .map((entry) => String(entry || '').trim())
        .filter((entry) => entry && /\d|[a-z]/iu.test(entry));
    }

    return [];
  }

  /** @param {unknown} value */
  #normalizeOwnedColors(value) {
    const source = Array.isArray(value)
      ? value
      : typeof value === 'string'
        ? this.#parseJsonArrayLenient(value)
        : [];

    const normalized = [];
    for (const entry of source) {
      if (entry && typeof entry === 'object') {
        const colorEntry = /** @type {{ v?: unknown, s?: unknown }} */ (entry);
        const resolvedValue = String(colorEntry.v || '').trim();
        const resolvedSwatch = String(colorEntry.s || resolvedValue).trim();
        if (!resolvedValue) continue;
        normalized.push({ value: resolvedValue, swatch: resolvedSwatch });
        continue;
      }

      const resolvedValue = String(entry || '').trim();
      if (!resolvedValue) continue;
      normalized.push({ value: resolvedValue, swatch: resolvedValue });
    }

    return normalized.filter((entry, index, entries) => entries.findIndex((item) => item.value === entry.value) === index);
  }

  /** @param {unknown} value */
  #normalizeBadgeImages(value) {
    const source = Array.isArray(value)
      ? value
      : typeof value === 'string'
        ? this.#parseJsonArrayLenient(value)
        : [];

    const normalized = [];
    for (const entry of source) {
      if (!entry || typeof entry !== 'object') continue;
      const image = /** @type {{ c?: string, u?: string }} */ (entry);
      const color = String(image.c || '').trim();
      const url = String(image.u || '').trim();
      if (!url) continue;
      normalized.push({ c: color, u: url });
    }

    if (normalized.length > 0 && Array.isArray(value)) {
      this.#debug('Badge images normalized', {
        sourceLength: source.length,
        normalizedLength: normalized.length,
        firstColor: normalized[0]?.c || null,
      });
    }

    return normalized;
  }

  /** @param {string} value */
  #decodeHtmlEntities(value) {
    if (!value || !value.includes('&')) return value;
    const textarea = document.createElement('textarea');
    textarea.innerHTML = value;
    return textarea.value;
  }

  /** @param {string} raw */
  #parseJsonObject(raw) {
    try {
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === 'object' ? parsed : null;
    } catch {
      return null;
    }
  }

  /** @param {string} raw */
  #parseBadgeImageData(raw) {
    try {
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return [];

      const results = [];
      for (const entry of parsed) {
        if (!entry || typeof entry !== 'object') continue;

        const normalizedEntry = /** @type {{ c?: string, u?: string }} */ (entry);
        const color = String(normalizedEntry.c || '').trim();
        const url = String(normalizedEntry.u || '').trim();
        if (!url) continue;

        results.push({ c: color, u: url });
      }

      return results;
    } catch {
      return [];
    }
  }

  /** @param {string | null | undefined} value */
  #normalizeValue(value) {
    return String(value || '').trim().toLowerCase().replace(/[^a-z0-9]/gu, '');
  }

  /** @param {string} value */
  #escapeHtml(value) {
    return String(value || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  /** @param {string} message */
  #renderError(message) {
    this.innerHTML = `<p class="badge-only-customizer__error">${this.#escapeHtml(message)}</p>`;
    this.#bottomPanel?.remove();
    this.#bottomPanel = null;
  }

  /** @returns {boolean} */
  #isDebugEnabled() {
    if (this.#debugEnabled !== null) return this.#debugEnabled;
    const debugValue = String(this.dataset.debug || '').trim().toLowerCase();
    this.#debugEnabled = debugValue === 'true' || debugValue === '1' || debugValue === 'yes';
    return this.#debugEnabled;
  }

  /**
   * @param {string} message
   * @param {unknown} [context]
   */
  #debug(message, context) {
    if (!this.#isDebugEnabled()) return;
    if (context === undefined) {
      console.info('[badge-only-customizer]', message);
      return;
    }
    console.info('[badge-only-customizer]', message, context);
  }
}

if (!customElements.get('badge-only-customizer-trigger')) {
  customElements.define('badge-only-customizer-trigger', BadgeOnlyCustomizerTrigger);
}
